import {
    IonCard,
    IonCardContent,
    IonCheckbox,
    IonCol,
    IonIcon,
    IonLabel,
    IonNote,
    IonRow,
    IonSelectOption,
    IonText,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import classNames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import { informationCircleOutline } from 'ionicons/icons';
import { useAtom } from 'jotai';
import React, { memo, useCallback, useMemo } from 'react';
import * as Yup from 'yup';

import LoadableContent from '../../components/content/LoadableContent';
import DefaultSelect from '../../components/forms/defaultselect/DefaultSelect';
import DefaultTextarea from '../../components/forms/defaulttextarea/DefaultTextarea';
import LoadingButton from '../../components/loadingbutton/LoadingButton';
import InterventionPage from '../../components/page/InterventionPage';
import PhotoUpload from '../../components/photoUpload/PhotoUpload';
import { TransServerMessage } from '../../components/translations/TransServerMessage';
import {
    accessTokenAtom,
    everythingCleanedAtom,
    jobDetailsWithHousePropertiesHouseTypesAndStateAtom,
    jobIdAtom,
    updateDBStateAtom,
} from '../../store/Atoms';
import { docRequest } from '../../store/Requests';
import { DeepomaticTaskGroups } from '../../types/Generic';
import { ImageCategories } from '../../types/ImageInfo';
import { MAX_IMAGE_NR, ODOO_INST_TYPE } from '../../utils/constants';
import { useCustomRouter } from '../../utils/hooks/useCustomRouter';
import usePhotosUploaded from '../../utils/hooks/usePhotosUploaded';

type TabDocFormValues = {
    installerComment: string;
    everythingCleaned: boolean;
    anotherInstallReq: boolean;
    houseType: string;
    isRepairJustified?: boolean;
};

const TabDocSchema = () =>
    Yup.object().shape({
        installerComment: Yup.string().required(t`Please enter comment`),
        everythingCleaned: Yup.boolean()
            .required(t`Please clean everything first`)
            .oneOf([true], t`Please clean everything first`),
        anotherInstallReq: Yup.boolean(),
        houseType: Yup.string().required(t`Please select house type`),
        isRepairJustified: Yup.boolean(),
    });

const TabDoc: React.FC = memo(() => {
    const router = useCustomRouter();
    const photosUploaded = usePhotosUploaded();
    const [accessToken] = useAtom(accessTokenAtom);
    const [jobId] = useAtom(jobIdAtom);
    const [presentToast] = useIonToast();
    const [value] = useAtom(
        jobDetailsWithHousePropertiesHouseTypesAndStateAtom
    );
    const [everythingCleaned, setEverythingCleaned] = useAtom(
        everythingCleanedAtom
    );
    const [, updateDBState] = useAtom(updateDBStateAtom);

    const isRepair = useMemo(
        () =>
            value.state === 'hasData'
                ? value.data.jobDetails.typeInstallation ===
                  ODOO_INST_TYPE.REPAIR
                : false,
        [value]
    );

    const houseTypes = useMemo(
        () => (value.state === 'hasData' ? value.data.houseTypes : []),
        [value]
    );
    const jobDetails = useMemo(
        () => (value.state === 'hasData' ? value.data.jobDetails : undefined),
        [value]
    );
    const houseProperties = useMemo(
        () => (value.state === 'hasData' ? value.data.houseProperties : {}),
        [value]
    );

    const handleFormSubmit = useCallback(
        async (
            data: TabDocFormValues,
            actions: FormikHelpers<TabDocFormValues>
        ) => {
            actions.setSubmitting(true);
            try {
                await docRequest(accessToken, jobId, {
                    installerComment: data.installerComment,
                    needIntervention: data.anotherInstallReq,
                    houseType: data.houseType,
                    isRepairJustified: isRepair
                        ? data.isRepairJustified
                        : undefined,
                });
                await updateDBState();
                router.push('signature', 'none');
            } catch (e) {
                presentToast({
                    message: t`Oops, something went wrong while saving form`,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            actions.setSubmitting(false);
        },
        [accessToken, jobId, isRepair, presentToast, router, updateDBState]
    );

    return (
        <InterventionPage title={t`Document`} withPadding>
            <LoadableContent value={value}>
                <Formik
                    initialValues={{
                        installerComment: '',
                        everythingCleaned: everythingCleaned,
                        anotherInstallReq:
                            jobDetails?.needIntervention || false,
                        houseType: houseProperties.houseType,
                        isRepairJustified:
                            jobDetails?.isRepairJustified || false,
                    }}
                    validationSchema={TabDocSchema()}
                    validateOnMount={true}
                    onSubmit={handleFormSubmit}
                >
                    {({
                        values,
                        handleChange,
                        handleBlur,
                        isSubmitting,
                        isValid,
                        setFieldValue,
                        errors,
                        touched,
                    }) => (
                        <Form>
                            <IonCard>
                                <IonCardContent>
                                    <IonText>
                                        <IonIcon
                                            icon={informationCircleOutline}
                                            size="large"
                                            className="ion-float-left ion-padding-end"
                                        ></IonIcon>
                                        <Trans>
                                            Complete and document the
                                            intervention. Please sign off on
                                            your work and ask the customer to
                                            confirm and sign off on the
                                            installation.
                                        </Trans>
                                    </IonText>
                                </IonCardContent>
                            </IonCard>
                            <PhotoUpload
                                className="ion-margin-top"
                                imageCode={ImageCategories.photosOfClosedPanel}
                                maxNumber={MAX_IMAGE_NR}
                                label={t`Photo of panel after installation`}
                                deepomaticTaskGroup={
                                    DeepomaticTaskGroups.ELECTRICAL_PANEL_AFTER
                                }
                                deepomaticWorkOrderId={
                                    jobDetails?.imageClassificationWorkOrderId
                                }
                            ></PhotoUpload>
                            <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top">
                                <IonCol>
                                    <IonLabel>
                                        <Trans>
                                            Is another intervention required?
                                        </Trans>
                                    </IonLabel>
                                </IonCol>
                                <IonCol size="auto">
                                    <IonCheckbox
                                        name="anotherInstallReq"
                                        checked={values.anotherInstallReq}
                                        onIonChange={() => {
                                            const newValue =
                                                !values.anotherInstallReq;
                                            setFieldValue(
                                                'anotherInstallReq',
                                                newValue
                                            );
                                        }}
                                    />
                                </IonCol>
                            </IonRow>
                            <PhotoUpload
                                className="ion-margin-top"
                                imageCode={ImageCategories.photosOther}
                                maxNumber={MAX_IMAGE_NR}
                                label={t`Other photos`}
                            ></PhotoUpload>
                            <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top ion-margin-bottom">
                                <IonCol>
                                    <IonLabel>
                                        <Trans>
                                            Has everything been cleaned?
                                        </Trans>
                                    </IonLabel>
                                </IonCol>
                                <IonCol size="auto">
                                    <IonCheckbox
                                        name="everythingCleaned"
                                        checked={values.everythingCleaned}
                                        onIonChange={() => {
                                            const newValue =
                                                !values.everythingCleaned;
                                            setEverythingCleaned(newValue);
                                            setFieldValue(
                                                'everythingCleaned',
                                                newValue
                                            );
                                        }}
                                    />
                                </IonCol>
                            </IonRow>
                            <DefaultSelect
                                className={classNames({
                                    'ion-invalid': errors.houseType,
                                    'ion-touched': touched.houseType,
                                })}
                                name="houseType"
                                value={values.houseType}
                                label={t`House type`}
                                placeholder={t`Select house type`}
                                onIonChange={handleChange}
                                onIonBlur={handleBlur}
                                errorText={errors.houseType}
                            >
                                {houseTypes.map((houseType) => (
                                    <IonSelectOption
                                        key={houseType.houseTypeId}
                                        value={houseType.houseTypeId}
                                    >
                                        <TransServerMessage
                                            id={houseType.houseTypeName}
                                        />
                                    </IonSelectOption>
                                ))}
                            </DefaultSelect>
                            {isRepair && (
                                <div>
                                    <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top">
                                        <IonCol>
                                            <IonLabel>
                                                <Trans>
                                                    Is the repair justified?
                                                </Trans>
                                            </IonLabel>
                                        </IonCol>
                                        <IonCol size="auto">
                                            <IonCheckbox
                                                name="isRepairJustified"
                                                checked={
                                                    values.isRepairJustified
                                                }
                                                onIonChange={() => {
                                                    const newValue =
                                                        !values.isRepairJustified;
                                                    setFieldValue(
                                                        'isRepairJustified',
                                                        newValue
                                                    );
                                                }}
                                            />
                                        </IonCol>
                                    </IonRow>
                                    <IonRow className="ion-margin-bottom">
                                        {!values.isRepairJustified && (
                                            <IonNote class="error">
                                                <Trans>
                                                    Please add your reason to
                                                    the installer comment
                                                </Trans>
                                            </IonNote>
                                        )}
                                    </IonRow>
                                </div>
                            )}
                            <DefaultTextarea
                                className={classNames({
                                    'ion-invalid': errors.installerComment,
                                    'ion-touched': touched.installerComment,
                                })}
                                name="installerComment"
                                value={values.installerComment}
                                label={t`Installer comment`}
                                onIonInput={handleChange}
                                errorText={errors.installerComment}
                            ></DefaultTextarea>
                            <LoadingButton
                                className="ion-margin-top"
                                disabled={!isValid || !photosUploaded}
                                loading={isSubmitting}
                                type="submit"
                            >
                                <Trans>Proceed</Trans>
                            </LoadingButton>
                        </Form>
                    )}
                </Formik>
            </LoadableContent>
        </InterventionPage>
    );
});

export default TabDoc;
